import React, { useEffect, useMemo, useState } from 'react';
import { Container, Spinner } from 'reactstrap';
import { Button } from '@mui/material';
import { getReceitasFromOikos } from '../../../utils/fc';
import { checkPendingNfse } from '../../../utils/kaminoNfse';
import ExecutiveSummary from './ContasAReceber/ExecutiveSummary';
import FaturamentoDashboard from './ContasAReceber/FaturamentoDashboard';
import RecebimentosDashboard from './ContasAReceber/RecebimentosDashboard';
import { monthRefDefault } from './ContasAReceber/utils';

export default function ContasAReceber({ initialFaturamentoView }) {
  const [dataIsLoading, setDataIsLoading] = useState(false);
  const [recs, setRecs] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  const [activeDomain, setActiveDomain] = useState('faturamento'); // faturamento | recebimentos
  const [mesRef, setMesRef] = useState(monthRefDefault());

  const [activeExecutiveKey, setActiveExecutiveKey] = useState('fat_prev'); // fat_prev|fat_real|liq_prev|liq_real|inadimplencia

  const [currentFaturamentoView, setCurrentFaturamentoView] = useState(initialFaturamentoView || 'dia');
  const [currentRecebimentoView, setCurrentRecebimentoView] = useState('recebidos_mes');

  const [selectedFaturamentoRecId, setSelectedFaturamentoRecId] = useState(null);
  const [selectedRecebimentoRecId, setSelectedRecebimentoRecId] = useState(null);

  useEffect(() => {
    if (!initialFaturamentoView) return
    setActiveDomain('faturamento')
    setCurrentFaturamentoView(initialFaturamentoView)
  }, [initialFaturamentoView])

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setDataIsLoading(true);
      try {
        try {
          await checkPendingNfse({ limit: 50 });
        } catch (e) {
          // Sync é best-effort (ex.: sem sessão PHP no domínio Oikos); a lista segue carregando.
          console.warn('[ContasAReceber] checkPendingNfse:', e?.message || e);
        }
        if (cancelled) return;
        const res = await getReceitasFromOikos();
        if (cancelled) return;
        setRecs(Array.isArray(res) ? res : []);
      } finally {
        if (!cancelled) setDataIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const goFaturamentoMes = () => {
    setActiveDomain('faturamento');
    setCurrentFaturamentoView('mes');
  };
  const goFaturamentoMesFaturados = () => {
    setActiveDomain('faturamento');
    setCurrentFaturamentoView('mes_faturados');
  };
  const goRecebidosMes = () => {
    setActiveDomain('recebimentos');
    setCurrentRecebimentoView('recebidos_mes');
  };
  const goRecebimentosPrevistoMes = () => {
    setActiveDomain('recebimentos');
    setCurrentRecebimentoView('previsto_mes');
  };
  const goAtraso = () => {
    setActiveDomain('recebimentos');
    setCurrentRecebimentoView('atraso');
  };

  const handleSelectExecutive = (key) => {
    setActiveExecutiveKey(key);
    if (key === 'fat_prev') goFaturamentoMes();
    else if (key === 'fat_real') goFaturamentoMesFaturados();
    else if (key === 'liq_prev') goRecebimentosPrevistoMes();
    else if (key === 'liq_real') goRecebidosMes();
    else if (key === 'inadimplencia') goAtraso();
  };

  const header = useMemo(() => {
    const isFat = activeDomain === 'faturamento';
    return (
      <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 900 }}>Domínio</div>
        <div className="d-flex" style={{ gap: 8 }}>
          <Button
            size="small"
            variant={isFat ? 'contained' : 'outlined'}
            onClick={() => setActiveDomain('faturamento')}
          >
            Faturamento
          </Button>
          <Button
            size="small"
            variant={!isFat ? 'contained' : 'outlined'}
            onClick={() => setActiveDomain('recebimentos')}
          >
            Recebimentos
          </Button>
        </div>
      </div>
    );
  }, [activeDomain]);

  if (dataIsLoading) {
    return (
      <div className="centered">
        <Spinner />
      </div>
    );
  }

  return (
    <Container fluid>
      <ExecutiveSummary
        recs={recs}
        mesRef={mesRef}
        setMesRef={setMesRef}
        activeExecutiveKey={activeExecutiveKey}
        onSelectExecutive={handleSelectExecutive}
      />

      {header}

      {activeDomain === 'recebimentos' && (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 6,
            border: '1px solid #f1b44c',
            backgroundColor: 'rgba(241,180,76,0.14)',
            color: '#7a5600',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Recebimentos em desenvolvimento: os números ainda precisam ser validados e devem ser considerados apenas para ilustração.
        </div>
      )}

      {activeDomain === 'faturamento' ? (
        <FaturamentoDashboard
          recs={recs}
          mesRef={mesRef}
          currentView={currentFaturamentoView}
          setCurrentView={setCurrentFaturamentoView}
          selectedRecId={selectedFaturamentoRecId}
          setSelectedRecId={setSelectedFaturamentoRecId}
          onNeedReload={() => setReloadKey((k) => k + 1)}
        />
      ) : (
        <RecebimentosDashboard
          recs={recs}
          mesRef={mesRef}
          currentView={currentRecebimentoView}
          setCurrentView={setCurrentRecebimentoView}
          selectedRecId={selectedRecebimentoRecId}
          setSelectedRecId={setSelectedRecebimentoRecId}
        />
      )}
    </Container>
  );
}
